import {
  SupportedCountry,
  PhoneInputProps,
  isValidPhoneNumber,
} from "@brexhq/component-library";
import React from "react";
import {
  useForm,
  UseFormTrigger,
  UseControllerProps,
  useController,
} from "react-hook-form";

import {
  EMPOWER_MULTI_STEP_LEAD_FORM,
  FormType,
  INVALID_INPUT_MESSAGE,
  PLEASE_USE_A_VALID_WEBSITE_MESSAGE,
} from "@brexhq/next/src/components/modules/LeadForm/sharedTypes";
import { accountPasswordValidation } from "@brexhq/next/src/hooks/useAccountPasswordValidation/useAccountPasswordValidation";
import { FieldDirtiedEvent } from "@brexhq/next/src/telemetry/analyticsEvents";
import { AnalyticsApi } from "@brexhq/next/src/telemetry/types";
import isValidEmail from "@brexhq/next/src/utils/isValidEmail";
import isValidURL from "@brexhq/next/src/utils/isValidURL";
import { LeadSelfAttestedType } from "@brexhq/next/types/brex-api";

import { localStorage } from "../../../../utils/localStorage";
import { LeadFormValues } from "../sharedTypes";

const sanitizePattern = {
  value: /^[^<>=*]*$/,
  message: INVALID_INPUT_MESSAGE,
};

export type LeadFormOptions = {
  /**
   * Set to false to bypass validation of required fields for debugging
   */
  requiredFields: boolean;
  /**
   * type of form being used
   */
  formType: FormType;
  mode: "onChange" | "onSubmit" | "onBlur";
};

/**
 * Create and configure props for form fields.
 */
export const useLeadFormFields = (
  trackEvent: AnalyticsApi["trackEvent"],
  options: LeadFormOptions,
) => {
  const [country, setCountry] = React.useState<SupportedCountry>("US");

  const {
    control,
    handleSubmit,
    trigger: _trigger,
    formState,
    getValues,
    setValue,
    clearErrors,
    resetField: _resetField,
    reset: _reset,
  } = useForm<LeadFormValues>({
    mode: options.mode,
  });

  // Decorate form's trigger method to allow us to deactivate validation
  const trigger: UseFormTrigger<LeadFormValues> = async (...args) => {
    if (options.requiredFields) {
      return _trigger(...args);
    } else {
      return true;
    }
  };

  const baseRules: UseControllerProps["rules"] = {
    required: {
      message: "This field is required.",
      value: options.requiredFields,
    },
  };

  // Convenience method to make building the form less verbose
  const useField = <T extends keyof LeadFormValues>(
    label: string,
    name: T,
    rules: UseControllerProps["rules"] = baseRules,
  ) => {
    const ctrl = useController({
      control,
      name,
      rules,
    });

    // Determine if we should show the error based on validation mode
    const shouldShowError = () => {
      if (!ctrl.fieldState.error) return false;

      switch (options.mode) {
        case "onChange":
          return true; // Always show errors immediately
        case "onBlur":
          return ctrl.fieldState.isTouched; // Only show after field has been blurred
        case "onSubmit":
          return formState.isSubmitted; // Only show after form submission
        default:
          return true;
      }
    };

    return {
      label,
      name: ctrl.field.name,
      onBlur: ctrl.field.onBlur,
      onChange: ({ value }) => {
        if (!ctrl.fieldState.isDirty) {
          trackEvent(
            new FieldDirtiedEvent(
              EMPOWER_MULTI_STEP_LEAD_FORM,
              ctrl.field.name,
            ),
          );
        }
        if (value === " ") {
          ctrl.field.onChange(value.trim());
        } else {
          ctrl.field.onChange(value);
        }
        /** Setting values in localStorage so users can continue on
         * will remove localstorage if value is empty (or erased)
         * just to make sure the form doesn't validate against ""
         */
        localStorage?.setItem(name, JSON.stringify(value));
        if (!value) {
          localStorage?.removeItem(name);
        }
      },
      value: ctrl.field.value as NonNullable<LeadFormValues[T]>,
      error: shouldShowError() ? ctrl.fieldState.error?.message : undefined,
    };
  };

  const onCountryChange: PhoneInputProps["onCountryChange"] = ({ value }) => {
    // Reset number when country is changed. Validation behavior is a bit unstable otherwise
    setValue("mobileNumber", "");
    // Ensure we are showing validation errors if appropriate since changing country doesn't actually dirty the mobile number field
    trigger("mobileNumber");
    setCountry(value);
  };

  const fields = {
    firstName: useField("First name", "firstName", {
      ...baseRules,
      pattern: sanitizePattern,
    }),
    lastName: useField("Last name", "lastName", {
      ...baseRules,
      pattern: sanitizePattern,
    }),
    workEmail: useField("Work email", "workEmail", {
      validate: () => {
        const value = getValues("workEmail") || "";
        if (!isValidEmail(value)) return "Please provide a valid email address";
        return true;
      },
    }),
    mobileNumber: {
      ...useField("Mobile number", "mobileNumber", {
        required: "Mobile number is required.",
        validate: (value) => {
          if (
            !isValidPhoneNumber({
              value,
              country,
            })
          ) {
            return "Invalid phone number.";
          }
          return true;
        },
      }),
      error: formState.errors.mobileNumber?.message,
      country,
      onCountryChange,
    },
    companyName: useField("Company name", "companyName", {
      ...baseRules,
      pattern: sanitizePattern,
    }),

    employeeCount: {
      ...useField("Employee count", "employeeCount"),
      label: undefined,
    },

    linkedinProfileUrl: {
      ...useField("LinkedIn URL", "linkedinProfileUrl"),
      label: "Your LinkedIn Profile URL",
    },

    monthlySales: {
      ...useField("Monthly sales", "monthlySales"),
      label: undefined,
    },

    isEcomm: {
      ...useField("Are you an e-commerce business?", "isEcomm"),
      label: undefined,
      options: [
        { label: "Yes", value: LeadSelfAttestedType.ECOMMERCE },
        { label: "No", value: LeadSelfAttestedType.OTHER },
      ],
    },

    fundingSource: useField("Primary source of funding", "fundingSource"),

    /**
     * TODO: [M7] deprecate validation rules once experiment is over
     */
    website: useField("Website", "website", {
      ...baseRules,
      validate: (value) => {
        if (!isValidURL(value)) return PLEASE_USE_A_VALID_WEBSITE_MESSAGE;
        return true;
      },
    }),

    password: useField("Enter password", "password", {
      ...baseRules,
      validate: (value) => {
        const { validationMessage, isValid } = accountPasswordValidation(value);
        if (!isValid) return validationMessage;
        return true;
      },
    }),

    fundingSources: useField("", "fundingSources", {
      ...baseRules,
    }),
  };

  const resetField = (
    field: keyof LeadFormValues | (keyof LeadFormValues)[],
  ) => {
    if (typeof field === "string") {
      return _resetField(field);
    }
    const mappedValues = {};

    (field as (keyof LeadFormValues)[]).forEach((field) => {
      mappedValues[field] = undefined;
    });

    return _reset(mappedValues);
  };

  return {
    fields,
    country,
    onCountryChange,
    getValues,
    setValue,
    trigger,
    formState,
    handleSubmit,
    clearErrors,
    resetField,
    control,
  };
};
